import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import HoverVideo from "./HoverVideo";
import { VideoMeta } from "../../../services/video.service";

import savedIcon from "../../../assets/MentalPlus/SaveFilled.png";

const STORAGE_KEY = "savedVideos";

type Props = {
  video: VideoMeta;
  isHover: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onClick: () => void;
  onToggleSaved?: () => void;
  savedOnly?: boolean;
};

const VideoCard = forwardRef<HTMLDivElement, Props>(
  (
    {
      video,
      isHover,
      onHoverStart,
      onHoverEnd,
      onClick,
      onToggleSaved,
      savedOnly,
    },
    ref
  ) => {
    const [isSaved, setIsSaved] = useState(false);

    const syncSavedState = () => {
      const savedVideos = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

      const alreadySaved = savedVideos.some(
        (item: any) => item?.uid === video.uid || item === video.uid
      );

      setIsSaved(alreadySaved);
    };

    useEffect(() => {
      syncSavedState();

      const handleFocus = () => {
        syncSavedState();
      };

      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          syncSavedState();
        }
      };

      const handleSavedVideosUpdated = () => {
        syncSavedState();
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("saved-videos-updated", handleSavedVideosUpdated);

      return () => {
        window.removeEventListener("focus", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("saved-videos-updated", handleSavedVideosUpdated);
      };
    }, [video.uid]);

    const handleRemoveSaved = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      const savedVideos = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

      const updatedVideos = savedVideos.filter(
        (item: any) => item?.uid !== video.uid && item !== video.uid
      );

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedVideos));
      setIsSaved(false);

      window.dispatchEvent(new Event("saved-videos-updated"));
      onToggleSaved?.();
    };

    return (
      <GridItem
        ref={ref}
        onMouseEnter={() => {
          onHoverStart();
          syncSavedState();
        }}
        onMouseLeave={onHoverEnd}
        onClick={onClick}
      >
        <ThumbContainer>
          <ThumbImg src={video.thumbnailUrl} alt="video thumbnail" />
          <HoverVideo hlsUrl={video.hlsUrl} isHover={isHover} />

          {(isSaved || savedOnly) && (
            <SaveButton
              type="button"
              onClick={handleRemoveSaved}
              aria-label="Retirer des vidéos enregistrées"
            >
              <SaveImg src={savedIcon} alt="video enregistrée" />
            </SaveButton>
          )}
        </ThumbContainer>

        <TitleSection>
          <VideoTitle>{video.meta?.title || video.meta?.name || "Sans titre"}</VideoTitle>
        </TitleSection>
      </GridItem>
    );
  }
);

VideoCard.displayName = "VideoCard";

export default VideoCard;

const ThumbContainer = styled.div`
  width: 100%;
  aspect-ratio: 4 / 5;
  position: relative;
  background: black;
  overflow: hidden;
`;

const GridItem = styled.div`
  background: #eef0f2;
  overflow: hidden;
  border-radius: 24px;
  width: 100%;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
`;

const TitleSection = styled.div`
  padding: 10px 10px 12px;
  min-height: 68px;
  background: #eef0f2;

  @media (min-width: 768px) {
    padding: 12px 12px 14px;
    min-height: 74px;
  }
`;

const VideoTitle = styled.p`
  margin: 0;
  color: #17243c;
  font-size: clamp(14px, 1.8vw, 18px);
  line-height: 1.18;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const SaveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 20;

  width: 24px;
  height: 24px;
  border: none;
  background: rgba(0, 0, 0, 0.32);
  backdrop-filter: blur(5px);
  border-radius: 999px;
  padding: 4px;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
`;

const SaveImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
`;